import React, { useEffect, useState } from 'react';
import { Card, Text, Button, makeStyles } from '@fluentui/react-components';
import { useMSALAuth } from '../contexts/MSALAuthContext';
import { useMsal } from '@azure/msal-react';
import { getRuntimeConfig } from '../config/runtimeConfig';
import { apiClient } from '../services/apiClient';

const useStyles = makeStyles({
  container: {
    padding: '20px'
  },
  card: {
    marginBottom: '20px',
    padding: '20px'
  },
  codeBlock: {
    backgroundColor: '#f0f0f0',
    padding: '10px',
    borderRadius: '4px',
    fontFamily: 'monospace',
    fontSize: '12px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all'
  }
});

export const DebugAuth: React.FC = () => {
  const styles = useStyles();
  const msalAuth = useMSALAuth();
  const { instance, accounts } = useMsal();
  const [tokenInfo, setTokenInfo] = useState<any>(null);

  useEffect(() => {
    // Try to get token info
    const getTokenInfo = async () => {
      if (accounts.length > 0) {
        try {
          const runtimeConfig = getRuntimeConfig();
          const response = await instance.acquireTokenSilent({
            scopes: runtimeConfig.apiScopes,
            account: accounts[0]
          });
          setTokenInfo({
            accessToken: response.accessToken?.substring(0, 50) + '...',
            expiresOn: response.expiresOn,
            scopes: response.scopes
          });
        } catch (error) {
          setTokenInfo({ error: error instanceof Error ? error.message : String(error) });
        }
      }
    };
    getTokenInfo();
  }, [accounts, instance]);

  const handleTestLogin = async () => {
    try {
      const runtimeConfig = getRuntimeConfig();
      await instance.loginPopup({
        scopes: runtimeConfig.apiScopes
      });
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const handleTestToken = async () => {
    try {
      const response = await apiClient.get<any>('/policies');
      console.log('API Response:', response);
      alert(
        `API Response: ${response.success ? 'success' : 'failure'} - ${
        JSON.stringify(response.data).substring(0, 100)}`

      );
    } catch (error) {
      console.error('API call failed:', error);
      alert(`API call failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  return (
    <div className={styles.container} data-testid="debug-auth-container-div">
      <h1 data-testid="debug-auth-h1">Authentication Debug Information</h1>

      <Card className={styles.card} data-testid="debug-auth-environment-card">
        <h2 data-testid="debug-auth-h2">Environment</h2>
        <div className={styles.codeBlock} data-testid="debug-auth-code-block-div">
          {JSON.stringify({
            NODE_ENV: process.env.NODE_ENV,
            hostname: window.location.hostname,
            isDevelopment: process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost',
            runtimeConfig: (() => {
              try {
                return getRuntimeConfig();
              } catch {
                return 'Runtime config not loaded yet';
              }
            })()
          }, null, 2)}
        </div>
      </Card>

      <Card className={styles.card} data-testid="debug-auth-msal-auth-context-card">
        <h2 data-testid="debug-auth-h2-2-h2">MSAL Auth Context</h2>
        <div className={styles.codeBlock} data-testid="debug-auth-code-block-2-div">
          {JSON.stringify({
            isAuthenticated: msalAuth.isAuthenticated,
            isLoading: msalAuth.isLoading,
            hasToken: !!msalAuth.token,
            tokenLength: msalAuth.token?.length || 0,
            user: msalAuth.user,
            error: msalAuth.error
          }, null, 2)}
        </div>
      </Card>

      <Card className={styles.card} data-testid="debug-auth-msal-instance-card">
        <h2 data-testid="debug-auth-h2-3-h2">MSAL Instance</h2>
        <div className={styles.codeBlock} data-testid="debug-auth-code-block-3-div">
          {JSON.stringify({
            accounts: accounts.map((a) => ({
              username: a.username,
              name: a.name,
              homeAccountId: a.homeAccountId.substring(0, 20) + '...'
            })),
            accountCount: accounts.length
          }, null, 2)}
        </div>
      </Card>

      <Card className={styles.card} data-testid="debug-auth-token-info-card">
        <h2 data-testid="debug-auth-h2-4-h2">Token Info</h2>
        <div className={styles.codeBlock} data-testid="debug-auth-code-block-4-div">
          {JSON.stringify(tokenInfo, null, 2)}
        </div>
      </Card>

      <Card className={styles.card} data-testid="debug-auth-actions-card">
        <h2 data-testid="debug-auth-h2-5-h2">Actions</h2>
        <div style={{ display: 'flex', gap: '10px' }} data-testid="debug-auth-actions-row-div">
          <Button onClick={handleTestLogin} data-testid="debug-auth-test-login-popup-button">Test Login Popup</Button>
          <Button onClick={handleTestToken} data-testid="debug-auth-test-api-call-button">Test API Call</Button>
          <Button onClick={() => instance.logoutPopup()} data-testid="debug-auth-logout-button">Logout</Button>
        </div>
      </Card>
    </div>);

};